/**
 * Annotation Store (Zustand)
 * Verwaltet Annotationen und Dokumente – mit Persistierung via FastAPI Backend.
 *
 * Migration Phase 2: Verwendet jetzt /api/v1/* CRUD-Endpunkte statt Bulk-Save.
 * Jede Mutation (create/update/delete) wird einzeln an das Backend gesendet.
 * localStorage-Fallback: Bei statischem Deploy (kein Backend) werden Daten
 * automatisch im Browser gespeichert und von dort wiederhergestellt.
 */

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { subscribeWithSelector } from 'zustand/middleware'
import { API_V1, apiFetch, projectUrl } from '../lib/apiBase'
import {
  Annotation,
  AnnotationDocument,
  AnnotationEditorState,
  AnnotationsData,
  BuildingData,
  EditorMode,
  EMPTY_ANNOTATIONS_DATA,
  EMPTY_BUILDING_DATA,
  EMPTY_TIPTAP_DOC,
  TipTapDocument,
  Vec3,
  AnnotationType,
} from '../types/annotations'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function now(): string {
  return new Date().toISOString()
}

/** Debounced save — verhindert Doppel-Saves bei schnellen Edits */
let _saveAnnotationTimer: ReturnType<typeof setTimeout> | null = null
function debouncedSave(fn: () => Promise<void>, delay = 300) {
  if (_saveAnnotationTimer) clearTimeout(_saveAnnotationTimer)
  _saveAnnotationTimer = setTimeout(fn, delay)
}

let _saveDocumentTimer: ReturnType<typeof setTimeout> | null = null
function debouncedDocSave(fn: () => Promise<void>, delay = 500) {
  if (_saveDocumentTimer) clearTimeout(_saveDocumentTimer)
  _saveDocumentTimer = setTimeout(fn, delay)
}

// ─── localStorage Fallback (statischer Deploy ohne Backend) ─────────────────

const LS_KEY_ANNOTATIONS = 'orto_store_annotations'
const LS_KEY_DOCUMENTS   = 'orto_store_documents'
/** Flag: Backend ist erreichbar → API-Calls machen Sinn */
let _backendAvailable = true

function _lsSave(key: string, data: unknown) {
  try { localStorage.setItem(key, JSON.stringify(data)) } catch { /* quota */ }
}

function _lsLoad<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) as T : null
  } catch { return null }
}

/** Nach jeder Mutation: State in localStorage sichern (debounced) */
let _lsPersistTimer: ReturnType<typeof setTimeout> | null = null
function _lsPersistState(getState: () => { annotations: Annotation[]; documents: AnnotationDocument[] }) {
  if (_lsPersistTimer) clearTimeout(_lsPersistTimer)
  _lsPersistTimer = setTimeout(() => {
    const s = getState()
    _lsSave(LS_KEY_ANNOTATIONS, s.annotations)
    _lsSave(LS_KEY_DOCUMENTS, s.documents)
    // BuildingData wird nur noch per Backend gespeichert (PUT /building-data)
  }, 500)
}

// ─── API Helpers ─────────────────────────────────────────────────────────────

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await apiFetch(projectUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`POST ${path}: HTTP ${res.status}`)
  const json = await res.json()
  return json.data || json
}

async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await apiFetch(projectUrl(path), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`PATCH ${path}: HTTP ${res.status}`)
  const json = await res.json()
  return json.data || json
}

async function apiDelete(path: string): Promise<void> {
  const res = await apiFetch(projectUrl(path), { method: 'DELETE' })
  if (!res.ok && res.status !== 204) throw new Error(`DELETE ${path}: HTTP ${res.status}`)
}

async function apiGet<T>(path: string): Promise<T> {
  const res = await apiFetch(projectUrl(path))
  if (!res.ok) throw new Error(`GET ${path}: HTTP ${res.status}`)
  const json = await res.json()
  return json.data || json
}

// ─── Geocoding (Nominatim) ────────────────────────────────────────────────────

let _geocodeTimer: ReturnType<typeof setTimeout> | null = null
function _geocodeDebounce(address: string, cb: (lat: number, lon: number) => void) {
  if (_geocodeTimer) clearTimeout(_geocodeTimer)
  _geocodeTimer = setTimeout(async () => {
    try {
      const q = encodeURIComponent(address)
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`, {
        headers: { 'Accept-Language': 'de' },
      })
      if (!res.ok) return
      const data = await res.json()
      if (data.length > 0) {
        cb(parseFloat(data[0].lat), parseFloat(data[0].lon))
      }
    } catch (err) {
      console.warn('[orto] Geocoding fehlgeschlagen:', err)
    }
  }, 1500)
}

// ─── BuildingData Save Helper ────────────────────────────────────────────────

async function _saveBuildingData(get: () => AnnotationStore) {
  try {
    const bd = get().buildingData
    const pid = bd.projectId
    if (!pid) { console.warn('[orto] BuildingData: kein projectId gesetzt'); return }
    await apiFetch(projectUrl(`/building-data`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields: bd.fields, lat: bd.lat ?? null, lon: bd.lon ?? null }),
    })
  } catch (err: any) {
    console.error('[orto] BuildingData speichern fehlgeschlagen:', err)
  }
}

// ─── Store Interface ──────────────────────────────────────────────────────────

interface AnnotationStore {
  // ── Data ──────────────────────────────────────────────────────────────────
  annotations: Annotation[]
  documents: AnnotationDocument[]
  buildingData: BuildingData
  isLoading: boolean
  isSaving: boolean
  lastSaveError: string | null
  /** Granular document save status for UI indicator */
  docSaveStatus: 'idle' | 'pending' | 'saving' | 'saved' | 'error'

  // ── UI State ──────────────────────────────────────────────────────────────
  editor: AnnotationEditorState
  /** Aktuell schwebende Annotation im Tooltip */
  hoveredAnnotationId: string | null

  // ── Filter State (shared between List + Layer) ──────────────────────────
  filterType: AnnotationType | 'all'
  searchQuery: string

  /** ID der Annotation, deren Popup geöffnet sein soll (z.B. via Listenklick) */
  selectedAnnotationId: string | null

  // ── Data Actions ──────────────────────────────────────────────────────────
  loadFromServer: () => Promise<void>
  /** @deprecated — Legacy Bulk-Save, nur noch als Fallback */
  saveToServer: () => Promise<void>

  // Annotations
  createAnnotation: (
    partial: Pick<Annotation, 'title' | 'tooltip' | 'type' | 'position' | 'camera' | 'cloudId'> &
      Partial<Pick<Annotation, 'tags' | 'body' | 'showPopup' | 'paperlessDocIds' | 'thumbnailMedium' | 'thumbnailHigh'>>
  ) => Annotation
  updateAnnotation: (id: string, patch: Partial<Omit<Annotation, 'id' | 'createdAt'>>) => void
  deleteAnnotation: (id: string) => void
  getAnnotation: (id: string) => Annotation | undefined
  getAnnotationsByCloud: (cloudId: string) => Annotation[]

  // Documents
  createDocument: (title: string, cloudId?: string) => AnnotationDocument
  updateDocument: (id: string, patch: Partial<Omit<AnnotationDocument, 'id' | 'createdAt'>>) => void
  deleteDocument: (id: string) => void
  getDocument: (id: string) => AnnotationDocument | undefined

  // Building Data
  loadBuildingData: (projectId: string) => Promise<void>
  updateBuildingField: (key: string, value: string) => void

  // Annotation helpers
  duplicateAnnotation: (id: string) => Annotation | undefined
  startRelocatingAnnotation: (id: string) => void

  // ── UI Actions ────────────────────────────────────────────────────────────
  setEditorMode: (mode: EditorMode) => void
  openAnnotationEditor: (annotationId: string) => void
  openAnnotationView: (annotationId: string) => void
  openDocumentEditor: (documentId: string) => void
  openDocumentViewer: (documentId: string) => void
  closeEditor: () => void
  startPlacingAnnotation: () => void
  setPendingPosition: (pos: Vec3 | null) => void
  setHoveredAnnotation: (id: string | null) => void
  setFilterType: (type: AnnotationType | 'all') => void
  setSearchQuery: (query: string) => void
  setSelectedAnnotation: (id: string | null) => void
}

// ─── Store Implementation ─────────────────────────────────────────────────────

export const useAnnotationStore = create<AnnotationStore>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // ── Initial State ──────────────────────────────────────────────────────
      annotations: [],
      documents: [],
      buildingData: { ...EMPTY_BUILDING_DATA },
      isLoading: false,
      isSaving: false,
      lastSaveError: null,
      docSaveStatus: 'idle' as const,
      hoveredAnnotationId: null,
      filterType: 'all' as AnnotationType | 'all',
      searchQuery: '',
      selectedAnnotationId: null,
      editor: {
        mode: 'none',
        activeAnnotationId: null,
        activeDocumentId: null,
        pendingPosition: null,
      },

      // ── Server I/O ─────────────────────────────────────────────────────────

      loadFromServer: async () => {
        set(s => { s.isLoading = true })

        try {
          // Lade Annotations und Documents parallel vom Backend
          const [annotations, documents] = await Promise.all([
            apiGet<Annotation[]>('/annotations'),
            apiGet<AnnotationDocument[]>('/documents'),
          ])

          set(s => {
            s.annotations = annotations ?? []
            s.documents = documents ?? []
          })

          // BuildingData wird jetzt separat per loadBuildingData(projectId) geladen
          // (pro Projekt, nicht mehr global "default")

        } catch (err) {
          console.warn('[orto] Backend nicht erreichbar, versuche Legacy-Endpunkt...')
          _backendAvailable = false
          // Fallback: Legacy Bulk-Endpunkt (Kompatibilität mit altem Backend)
          try {
            const res = await apiFetch('/api/annotations')
            if (res.ok) {
              _backendAvailable = true
              const raw = await res.json()
              const data: AnnotationsData = raw.data || raw
              set(s => {
                s.annotations = data.annotations ?? []
                s.documents = data.documents ?? []
              })
            } else { throw new Error('legacy 404') }
          } catch {
            // Fallback 2: statische Datei
            try {
              const res = await fetch('/annotations.json')
              if (res.ok) {
                const data: AnnotationsData = await res.json()
                set(s => {
                  s.annotations = data.annotations ?? []
                  s.documents = data.documents ?? []
                })
              } else { throw new Error('static 404') }
            } catch {
              // Fallback 3: localStorage (statischer Deploy)
              const lsAnnotations = _lsLoad<Annotation[]>(LS_KEY_ANNOTATIONS)
              const lsDocuments   = _lsLoad<AnnotationDocument[]>(LS_KEY_DOCUMENTS)
              if (lsAnnotations || lsDocuments) {
                console.log('[orto] Daten aus localStorage geladen')
                set(s => {
                  s.annotations = lsAnnotations ?? []
                  s.documents = lsDocuments ?? []
                })
              }
            }
          }
        } finally {
          set(s => { s.isLoading = false })
        }
      },

      /** Legacy Bulk-Save als Fallback */
      saveToServer: async () => {
        const state = get()
        set(s => { s.isSaving = true; s.lastSaveError = null })
        const payload: AnnotationsData = {
          version: 1,
          annotations: state.annotations,
          documents: state.documents,
          // buildingData wird separat per PUT /building-data gespeichert
        }
        try {
          const res = await apiFetch('/api/annotations', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Unbekannter Fehler'
          console.error('[orto] Speichern fehlgeschlagen:', msg)
          set(s => { s.lastSaveError = msg })
        } finally {
          set(s => { s.isSaving = false })
        }
      },

      // ── Annotation CRUD ────────────────────────────────────────────────────

      createAnnotation: (partial) => {
        const id = generateId()
        const annotation: Annotation = {
          id,
          title: partial.title,
          tooltip: partial.tooltip,
          body: partial.body ?? EMPTY_TIPTAP_DOC,
          type: partial.type,
          tags: partial.tags ?? [],
          position: partial.position,
          camera: partial.camera,
          cloudId: partial.cloudId,
          showPopup: partial.showPopup ?? true,
          paperlessDocIds: partial.paperlessDocIds ?? [],
          thumbnailMedium: partial.thumbnailMedium,
          thumbnailHigh: partial.thumbnailHigh,
          createdAt: now(),
          updatedAt: now(),
        }

        // Optimistic: sofort in State
        set(s => { s.annotations.push(annotation) })

        // WICHTIG: setTimeout(0) um den fetch AUSSERHALB des Zustand/Immer
        // Execution-Kontexts auszuführen — sonst wird der Request nicht gesendet.
        const payload = JSON.parse(JSON.stringify({
          id,
          title: annotation.title,
          tooltip: annotation.tooltip,
          body: annotation.body,
          type: annotation.type,
          tags: annotation.tags,
          position: annotation.position,
          camera: annotation.camera,
          cloudId: annotation.cloudId,
          showPopup: annotation.showPopup,
          paperlessDocIds: annotation.paperlessDocIds,
          thumbnailMedium: annotation.thumbnailMedium || undefined,
          thumbnailHigh: annotation.thumbnailHigh || undefined,
        }))
        setTimeout(() => {
          apiPost('/annotations', payload).catch(err => {
            console.error('[orto] Annotation erstellen fehlgeschlagen:', err)
          })
        }, 0)

        return annotation
      },

      updateAnnotation: (id, patch) => {
        // Optimistic: sofort updaten
        set(s => {
          const idx = s.annotations.findIndex(a => a.id === id)
          if (idx === -1) return
          Object.assign(s.annotations[idx], patch, { updatedAt: now() })
        })

        // Debounced: an Backend senden
        debouncedSave(async () => {
          try {
            await apiPatch(`/annotations/${id}`, patch)
          } catch (err: any) {
            console.error('[orto] Annotation update fehlgeschlagen:', err)
            set(s => { s.lastSaveError = err.message })
          }
        })
      },

      deleteAnnotation: (id) => {
        // Optimistic: sofort aus State entfernen
        set(s => {
          s.annotations = s.annotations.filter(a => a.id !== id)
          s.documents.forEach(doc => {
            doc.linkedAnnotationIds = (doc.linkedAnnotationIds ?? []).filter(lid => lid !== id)
          })
          if (s.editor.activeAnnotationId === id) {
            s.editor.mode = 'none'
            s.editor.activeAnnotationId = null
          }
        })

        // setTimeout(0) — Zustand/Immer-Kontext verlassen
        setTimeout(() => {
          apiDelete(`/annotations/${id}`).catch(err => {
            console.error('[orto] Annotation löschen fehlgeschlagen:', err)
          })
        }, 0)
      },

      getAnnotation: (id) => get().annotations.find(a => a.id === id),

      getAnnotationsByCloud: (cloudId) =>
        get().annotations.filter(a => a.cloudId === cloudId),

      // ── Building Data (pro Projekt) ────────────────────────────────────────
      loadBuildingData: async (projectId) => {
        try {
          const bd = await apiGet<BuildingData>(`/building-data`)
          if (bd) {
            set(s => { s.buildingData = { ...bd, projectId } })
          }
        } catch {
          // Noch keine Daten für dieses Projekt
          set(s => { s.buildingData = { ...EMPTY_BUILDING_DATA, projectId } })
        }
      },

      updateBuildingField: (key, value) => {
        set(s => {
          s.buildingData.fields[key] = value
          s.buildingData.updatedAt = now()
        })

        // Bei Adressänderung: Geocoding auslösen
        if (key === 'ort' && value.trim().length > 5) {
          _geocodeDebounce(value.trim(), (lat, lon) => {
            set(s => { s.buildingData.lat = lat; s.buildingData.lon = lon })
            // Koordinaten mitspeichern
            _saveBuildingData(get)
          })
        }

        // Debounced save to backend
        debouncedSave(async () => _saveBuildingData(get))
      },

      duplicateAnnotation: (id) => {
        const source = get().getAnnotation(id)
        if (!source) return undefined
        const dup: Annotation = {
          ...JSON.parse(JSON.stringify(source)),
          id: generateId(),
          title: `${source.title} (Kopie)`,
          createdAt: now(),
          updatedAt: now(),
        }
        set(s => { s.annotations.push(dup) })

        // setTimeout(0) — Zustand/Immer-Kontext verlassen
        const dupPayload = JSON.parse(JSON.stringify({
          id: dup.id,
          title: dup.title,
          tooltip: dup.tooltip,
          body: dup.body,
          type: dup.type,
          tags: dup.tags,
          position: dup.position,
          camera: dup.camera,
          cloudId: dup.cloudId,
          showPopup: dup.showPopup,
          paperlessDocIds: dup.paperlessDocIds,
        }))
        setTimeout(() => {
          apiPost('/annotations', dupPayload).catch(err => {
            console.error('[orto] Annotation duplizieren fehlgeschlagen:', err)
          })
        }, 0)

        return dup
      },

      startRelocatingAnnotation: (id) =>
        set(s => {
          s.editor.mode = 'relocate'
          s.editor.activeAnnotationId = id
          s.editor.activeDocumentId = null
          s.editor.pendingPosition = null
          s.selectedAnnotationId = null
        }),

      // ── Document CRUD ──────────────────────────────────────────────────────

      createDocument: (title, cloudId) => {
        const id = generateId()
        const doc: AnnotationDocument = {
          id,
          title,
          content: EMPTY_TIPTAP_DOC,
          linkedAnnotationIds: [],
          cloudId,
          createdAt: now(),
          updatedAt: now(),
        }

        set(s => { s.documents.push(doc) })

        // setTimeout(0) — Zustand/Immer-Kontext verlassen
        const docPayload = JSON.parse(JSON.stringify({
          id,
          title,
          content: doc.content,
          cloudId,
          linkedAnnotationIds: [],
        }))
        setTimeout(() => {
          apiPost('/documents', docPayload).catch(err => {
            console.error('[orto] Document erstellen fehlgeschlagen:', err)
          })
        }, 0)

        return doc
      },

      updateDocument: (id, patch) => {
        set(s => {
          const idx = s.documents.findIndex(d => d.id === id)
          if (idx === -1) return
          Object.assign(s.documents[idx], patch, { updatedAt: now() })
          if (patch.content) {
            s.documents[idx].linkedAnnotationIds = extractAnnotationIds(patch.content)
          }
        })

        // Debounced — Documents werden oft schnell hintereinander aktualisiert (Typing)
        set(s => { s.docSaveStatus = 'pending' })
        debouncedDocSave(async () => {
          set(s => { s.docSaveStatus = 'saving' })
          try {
            const doc = get().getDocument(id)
            if (!doc) return
            await apiPatch(`/documents/${id}`, {
              ...patch,
              linkedAnnotationIds: doc.linkedAnnotationIds,
            })
            set(s => { s.docSaveStatus = 'saved' })
            // Reset to idle after a short display period
            setTimeout(() => {
              if (get().docSaveStatus === 'saved') set(s => { s.docSaveStatus = 'idle' })
            }, 2000)
          } catch (err: any) {
            console.error('[orto] Document update fehlgeschlagen:', err)
            set(s => { s.lastSaveError = err.message; s.docSaveStatus = 'error' })
          }
        })
      },

      deleteDocument: (id) => {
        set(s => {
          s.documents = s.documents.filter(d => d.id !== id)
          if (s.editor.activeDocumentId === id) {
            s.editor.mode = 'none'
            s.editor.activeDocumentId = null
          }
        })

        // setTimeout(0) — Zustand/Immer-Kontext verlassen
        setTimeout(() => {
          apiDelete(`/documents/${id}`).catch(err => {
            console.error('[orto] Document löschen fehlgeschlagen:', err)
          })
        }, 0)
      },

      getDocument: (id) => get().documents.find(d => d.id === id),

      // ── UI Actions ─────────────────────────────────────────────────────────

      setEditorMode: (mode) => set(s => { s.editor.mode = mode }),

      openAnnotationEditor: (annotationId) =>
        set(s => {
          s.editor.mode = 'edit'
          s.editor.activeAnnotationId = annotationId
          s.editor.activeDocumentId = null
          s.selectedAnnotationId = null
        }),

      openAnnotationView: (annotationId) =>
        set(s => {
          s.editor.mode = 'view'
          s.editor.activeAnnotationId = annotationId
          s.editor.activeDocumentId = null
        }),

      openDocumentEditor: (documentId) =>
        set(s => {
          s.editor.mode = 'edit'
          s.editor.activeDocumentId = documentId
          s.editor.activeAnnotationId = null
          s.selectedAnnotationId = null
        }),

      openDocumentViewer: (documentId) =>
        set(s => {
          s.editor.mode = 'view'
          s.editor.activeDocumentId = documentId
          s.editor.activeAnnotationId = null
          s.selectedAnnotationId = null
        }),

      closeEditor: () =>
        set(s => {
          s.editor.mode = 'none'
          s.editor.activeAnnotationId = null
          s.editor.activeDocumentId = null
          s.editor.pendingPosition = null
          s.selectedAnnotationId = null
        }),

      startPlacingAnnotation: () =>
        set(s => {
          s.editor.mode = 'place'
          s.editor.activeAnnotationId = null
          s.editor.activeDocumentId = null
          s.editor.pendingPosition = null
          s.selectedAnnotationId = null
        }),

      setPendingPosition: (pos) =>
        set(s => { s.editor.pendingPosition = pos }),

      setHoveredAnnotation: (id) =>
        set(s => { s.hoveredAnnotationId = id }),

      setFilterType: (type) =>
        set(s => { s.filterType = type }),

      setSearchQuery: (query) =>
        set(s => { s.searchQuery = query }),

      setSelectedAnnotation: (id) =>
        set(s => { s.selectedAnnotationId = id }),
    }))
  )
)

// ─── Utility: Extract Annotation IDs from TipTap Doc ─────────────────────────

function extractAnnotationIds(doc: TipTapDocument): string[] {
  const ids: string[] = []
  function walk(nodes: typeof doc.content) {
    for (const node of nodes ?? []) {
      if (node.type === 'annotationLink' && node.attrs?.annotationId) {
        ids.push(node.attrs.annotationId as string)
      }
      if (node.content) walk(node.content)
    }
  }
  walk(doc.content)
  return [...new Set(ids)]
}

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectActiveAnnotation = (s: AnnotationStore) =>
  s.editor.activeAnnotationId ? s.getAnnotation(s.editor.activeAnnotationId) : null

export const selectActiveDocument = (s: AnnotationStore) =>
  s.editor.activeDocumentId ? s.getDocument(s.editor.activeDocumentId) : null

// ─── Auto-Persist nach localStorage (für statischen Deploy) ─────────────────
useAnnotationStore.subscribe(
  (s) => ({ a: s.annotations, d: s.documents }),
  () => _lsPersistState(useAnnotationStore.getState),
  { equalityFn: (a, b) => a.a === b.a && a.d === b.d },
)
